import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { LOADING_SKELETON_ROWS } from '../constants';
import { addExpandableAttributes } from '../helpers';
import { generateLoadingAttributes } from '../loading';
import type { DataWithRowId } from '../types';

interface UseTableDataProps<T extends object, K extends object = T> {
  data: T[];
  loading: boolean;
  rowKey?: string;
  tanstackColumns: ColumnDef<DataWithRowId<T, K>, unknown>[];
}

interface UseTableDataReturn<T extends object, K extends object = T> {
  displayColumns: ColumnDef<DataWithRowId<T, K>, unknown>[];
  displayData: DataWithRowId<T, K>[];
  preparedData: DataWithRowId<T, K>[];
}

export const useTableData = <T extends object, K extends object = T>({
  data,
  loading,
  rowKey,
  tanstackColumns
}: UseTableDataProps<T, K>): UseTableDataReturn<T, K> => {
  // Prepare data with expandable attributes and unique row identifiers
  const preparedData = useMemo(() => {
    const tableData = loading ? [] : data;

    return addExpandableAttributes(tableData as DataWithRowId<T, K>[], rowKey);
  }, [loading, data, rowKey]);

  // Generate loading columns and data - only recompute when columns change
  const { loadingColumns, loadingData } = useMemo(
    () => generateLoadingAttributes(tanstackColumns, LOADING_SKELETON_ROWS, String(rowKey)),
    [tanstackColumns, rowKey]
  );

  // Memoize display columns to prevent unnecessary re-renders
  const displayColumns: ColumnDef<DataWithRowId<T, K>, unknown>[] = useMemo(
    () => (loading ? (loadingColumns as ColumnDef<DataWithRowId<T, K>, unknown>[]) : tanstackColumns),
    [loading, loadingColumns, tanstackColumns]
  );

  // Memoize display data based on loading state
  const displayData = useMemo(
    () => (loading ? (loadingData as unknown as DataWithRowId<T, K>[]) : preparedData),
    [loading, loadingData, preparedData]
  );

  return {
    preparedData,
    displayColumns,
    displayData
  };
};
